import React, { useState } from "react";
import {
  MultiCheckBoxComponent,
  ValidationRule,
  FlexDirection,
} from "../../services/types/ComponentTypes";

interface MultiCheckBoxProps {
  config: MultiCheckBoxComponent;
  value?: (string | number | boolean)[];
  onChange?: (name: string, value: (string | number | boolean)[]) => void;
}

export const MultiCheckBox: React.FC<MultiCheckBoxProps> = ({
  config,
  value = [],
  onChange,
}) => {
  const [error, setError] = useState<string>("");
  const [selectedValues, setSelectedValues] = useState<
    (string | number | boolean)[]
  >([]);

  // Sync with value prop
  React.useEffect(() => {
    let newValues: (string | number | boolean)[] = [];

    if (value && value.length > 0) {
      if (typeof value[0] === "object" && value[0] !== null) {
        // Handle array of objects - extract value using Json_Attribute
        const jsonAttr = (config as any).Json_Attribute || "id";
        // Normalize to string to ensure matching works regardless of API/Lookup mismatch
        newValues = value.map((item: any) => String(item[jsonAttr] || item.id || item.value));
      } else {
        // Handle array of primitives - normalize to string
        newValues = (value as (string | number | boolean)[]).map(String);
      }
    } else if (config.defaultValue) {
      newValues = (config.defaultValue as any[]).map(String);
    }

    setSelectedValues(newValues);
  }, [value, (config as any).Json_Attribute]);

  const validateData = (
    dataToValidate: (string | number | boolean)[]
  ): string => {
    if (!config.validation) return "";

    for (const rule of config.validation) {
      switch (rule.rule) {
        case ValidationRule.REQUIRED:
          if (dataToValidate.length === 0)
            return rule.message || "At least one option must be selected";
          break;
        case ValidationRule.MIN_VALUE:
          if (dataToValidate.length < (rule.value as number))
            return rule.message || `Minimum ${rule.value} selections required`;
          break;
        case ValidationRule.MAX_VALUE:
          if (dataToValidate.length > (rule.value as number))
            return rule.message || `Maximum ${rule.value} selections allowed`;
          break;
      }
    }
    return "";
  };

  const handleSelectionChange = (
    optionValue: string | number | boolean,
    checked: boolean
  ) => {
    let newSelectedValues: (string | number | boolean)[];
    const stringOptionValue = String(optionValue);

    if (checked) {
      if (!selectedValues.some(v => String(v) === stringOptionValue)) {
        if (
          config.maxSelections &&
          selectedValues.length >= config.maxSelections
        ) {
          return;
        }
        // Store as string for consistency
        newSelectedValues = [...selectedValues, stringOptionValue];
      } else {
        return;
      }
    } else {
      newSelectedValues = selectedValues.filter((val) => String(val) !== stringOptionValue);
    }

    setSelectedValues(newSelectedValues);
    const validationError = validateData(newSelectedValues);
    setError(validationError);
    onChange?.(config.name, newSelectedValues);
  };

  const containerStyles: React.CSSProperties = {
    backgroundColor: config.style?.backgroundColor || "transparent",
    padding: config.style?.padding || 16,
    borderRadius: config.style?.borderRadius || 8,
    margin: config.style?.margin || 0,
  };

  return (
    <div
      className={`border ${error ? "border-2 border-danger-500 dark:border-danger-500" : "border-gray-300 dark:border-gray-600"
        }`}
      style={containerStyles}
    >
      {config.label && (
        <label className="block mb-1 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          {config.label}
          {config.required && <span className="text-danger-500 dark:text-danger-400 ml-1">*</span>}
        </label>
      )}

      <div
        className={`flex ${config.direction === FlexDirection.ROW
          ? "flex-row flex-wrap"
          : "flex-row flex-wrap"
          } gap-3`}
      >
        {!config.options || !Array.isArray(config.options) || config.options.length === 0 ? (
          <div className="text-gray-500 dark:text-gray-400 text-sm py-2">
            {config.lookupName || config.lookupName
              ? "Loading options..."
              : "No options available"}
          </div>
        ) : (
          config.options.map((option, index) => {
            const isSelected = selectedValues.some(val => String(val) === String(option.value));
            const isDisabled = option.disabled || config.disabled;

            return (
              <div
                key={index}
                className={`flex items-center p-2 rounded-md transition-colors duration-200 ${isDisabled
                  ? "cursor-not-allowed opacity-70"
                  : "cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                  }`}
                onClick={() =>
                  !isDisabled &&
                  handleSelectionChange(option.value, !isSelected)
                }
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) =>
                    !isDisabled &&
                    handleSelectionChange(option.value, e.target.checked)
                  }
                  disabled={isDisabled}
                  className="w-4 h-4 mr-2 cursor-pointer accent-success-600 dark:accent-success-500"
                />
                <span className="text-sm font-normal text-gray-900 dark:text-gray-100 cursor-pointer select-none font-sans">
                  {option.label}
                </span>
              </div>
            );
          })
        )}
      </div>

      {error && (
        <div className="text-danger-500 dark:text-danger-400 text-sm mt-3 flex items-center">
          <span className="mr-1">⚠️</span>
          {error}
        </div>
      )}

      {config.helperText && !error && (
        <div className="text-gray-500 dark:text-gray-400 text-sm mt-3">{config.helperText}</div>
      )}

      {config.maxSelections && (
        <div className="text-gray-500 dark:text-gray-400 text-xs mt-2 text-right">
          {selectedValues.length}/{config.maxSelections} selected
        </div>
      )}
    </div>
  );
};
